import type { ScreeningExclusion } from "./bookingFlowMachine"
import { bookingFlowMachine } from "./bookingFlowMachine"
import { ScreeningProgram, useConsentStatus } from "./hooks"
import { BookedPage } from "./pages/BookedPage"
import { ConsentPage } from "./pages/ConsentPage"
import { HealthQuestionsPage } from "./pages/HealthQuestionsPage"
import { NoAvailabilityPage } from "./pages/NoAvailabilityPage"
import { NotEligiblePage } from "./pages/NotEligiblePage"
import { SeeAvailabilityPage } from "./pages/SeeAvailabilityPage"
import { Box, Progress } from "@carrotfertility/carotene-core"
import { useMachine } from "@xstate/react"
import React from "react"

type BookingFlowProps = {
  // The program the member is booking into — drives the screening question set.
  program: ScreeningProgram
  // Navigation is the host's concern — these come in as callbacks rather than the
  // package importing the router.
  onExit?: () => void
  onReturnToProviderResults?: () => void
}

// Resolves whether the consent page should be shown before the machine starts, so the
// machine's `alreadyConsented` guard has a real value at init.
export function BookingFlow({ program, onExit, onReturnToProviderResults }: BookingFlowProps): JSX.Element {
  const consentStatus = useConsentStatus()

  if (consentStatus.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }

  // On a status-check failure, don't block booking — treat as not-yet-signed and show
  // the consent page.
  const alreadySignedConsent = consentStatus.data?.hasSignedAll ?? false

  return (
    <BookingMachine
      program={program}
      alreadySignedConsent={alreadySignedConsent}
      onExit={onExit}
      onReturnToProviderResults={onReturnToProviderResults}
    />
  )
}

function BookingMachine({
  program,
  alreadySignedConsent,
  onExit,
  onReturnToProviderResults
}: BookingFlowProps & { alreadySignedConsent: boolean }): JSX.Element | null {
  const [state, send] = useMachine(bookingFlowMachine, { input: { alreadySignedConsent, program } })
  const onContinue = () => send({ type: "CONTINUE" })
  const onDisqualified = (exclusion: ScreeningExclusion) => send({ type: "DISQUALIFIED", exclusion })

  if (state.matches("consent"))
    return <ConsentPage onContinue={onContinue} onReturnToProviderResults={onReturnToProviderResults} />
  if (state.matches("healthQuestions"))
    return (
      <HealthQuestionsPage
        program={program}
        onContinue={onContinue}
        onDisqualified={onDisqualified}
        onReturnToProviderResults={onReturnToProviderResults}
      />
    )
  if (state.matches("seeAvailability")) return <SeeAvailabilityPage onContinue={onContinue} />
  if (state.matches("booked")) return <BookedPage onBackToHome={onExit} />
  if (state.matches("notEligible")) return <NotEligiblePage exclusion={state.context.exclusion} onBackToHome={onExit} />
  if (state.matches("noAvailability")) return <NoAvailabilityPage onBackToHome={onExit} />
  return null
}
